import { useCallback, useEffect, useRef, useState } from 'react';

// --- Message Types ---

/** Client → Server message actions */
export type ClientAction = 'move' | 'resign' | 'play_again' | 'exit';

/** Client → Server message payloads */
export interface ClientMoveMessage {
  action: 'move';
  cell: number;
  seq: number;
}

export interface ClientResignMessage {
  action: 'resign';
  seq: number;
}

export interface ClientPlayAgainMessage {
  action: 'play_again';
  seq: number;
}

export interface ClientExitMessage {
  action: 'exit';
  seq: number;
}

export type ClientMessage =
  | ClientMoveMessage
  | ClientResignMessage
  | ClientPlayAgainMessage
  | ClientExitMessage;

/** Server → Client event types */
export type ServerEvent =
  | 'game_start'
  | 'board_update'
  | 'game_end'
  | 'turn_start'
  | 'opponent_left'
  | 'error'
  | 'seq_ack';

export interface GameStartPayload {
  gameId: string;
  opponent: { nickname: string; avatar: string };
  myAvatar: string;
  youGoFirst: boolean;
  board: (string | null)[];
  isYourTurn: boolean;
}

export interface BoardUpdatePayload {
  board: (string | null)[];
  isYourTurn: boolean;
  lastMove: number | null;
  scores: { you: number; opponent: number };
}

export interface GameEndPayload {
  result: 'win' | 'lose' | 'draw' | 'forfeit' | 'resign';
  board: (string | null)[];
  winningCells: number[] | null;
  scores: { you: number; opponent: number };
}

export interface TurnStartPayload {
  isYourTurn: boolean;
  timerStart?: string;
}

export interface OpponentLeftPayload {
  reason: string;
}

export interface ErrorPayload {
  code: string;
  message: string;
}

export interface SeqAckPayload {
  seq: number;
}

export interface ServerMessage {
  event: ServerEvent;
  payload:
    | GameStartPayload
    | BoardUpdatePayload
    | GameEndPayload
    | TurnStartPayload
    | OpponentLeftPayload
    | ErrorPayload
    | SeqAckPayload;
}

// --- Hook Configuration ---

export interface UseWebSocketOptions {
  /** Base URL for the REST API (e.g. https://xox.pragmaticmushroom.com/api) */
  apiBaseUrl: string;
  /** WebSocket endpoint URL (e.g. wss://ws.xox.pragmaticmushroom.com) */
  wsBaseUrl: string;
  /** Function that returns the current auth token */
  getAuthToken: () => Promise<string>;
  /** Maximum reconnection attempts (default: 3) */
  maxRetries?: number;
}

export interface UseWebSocketReturn {
  /** Initiate the WebSocket connection */
  connect: () => void;
  /** Close the WebSocket connection */
  disconnect: () => void;
  /** Send a message (auto-increments sequence number) */
  sendMessage: (action: ClientAction, payload?: Record<string, unknown>) => void;
  /** Most recent message received from the server */
  lastMessage: ServerMessage | null;
  /** Whether the WebSocket is currently connected */
  isConnected: boolean;
  /** Whether the client is attempting to reconnect */
  isReconnecting: boolean;
}

// --- Constants ---

const DEFAULT_MAX_RETRIES = 3;
const BASE_BACKOFF_MS = 1000;

// --- Hook Implementation ---

export function useWebSocket(options: UseWebSocketOptions): UseWebSocketReturn {
  const { apiBaseUrl, wsBaseUrl, getAuthToken, maxRetries = DEFAULT_MAX_RETRIES } = options;

  const [lastMessage, setLastMessage] = useState<ServerMessage | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);

  const wsRef = useRef<WebSocket | null>(null);
  const seqRef = useRef<number>(0);
  const ackedSeqRef = useRef<number>(0);
  const retryCountRef = useRef<number>(0);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const intentionalCloseRef = useRef<boolean>(false);
  const mountedRef = useRef<boolean>(true);

  /** Request a short-lived connection ticket from the REST API */
  const requestTicket = useCallback(async (): Promise<string> => {
    const token = await getAuthToken();
    const response = await fetch(`${apiBaseUrl}/ws-ticket`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to obtain WebSocket ticket: ${response.status}`);
    }

    const data = await response.json();
    return data.ticketId;
  }, [apiBaseUrl, getAuthToken]);

  /** Handle incoming WebSocket messages */
  const handleMessage = useCallback((event: MessageEvent) => {
    try {
      const message: ServerMessage = JSON.parse(event.data);

      // Handle seq_ack to track acknowledged sequences
      if (message.event === 'seq_ack') {
        const ackPayload = message.payload as SeqAckPayload;
        ackedSeqRef.current = ackPayload.seq;
      }

      setLastMessage(message);
    } catch {
      // Silently ignore malformed messages
    }
  }, []);

  /** Attempt reconnection with exponential backoff */
  const attemptReconnect = useCallback(() => {
    if (!mountedRef.current) return;
    if (retryCountRef.current >= maxRetries) {
      setIsReconnecting(false);
      return;
    }

    setIsReconnecting(true);
    const delay = BASE_BACKOFF_MS * Math.pow(2, retryCountRef.current);
    retryCountRef.current += 1;

    retryTimeoutRef.current = setTimeout(() => {
      if (mountedRef.current) {
        connectInternal();
      }
    }, delay);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxRetries]);

  /** Internal connect logic (used for initial connect and reconnect) */
  const connectInternal = useCallback(async () => {
    try {
      const ticketId = await requestTicket();
      const ws = new WebSocket(`${wsBaseUrl}?ticket=${ticketId}`);

      ws.onopen = () => {
        if (!mountedRef.current) {
          ws.close();
          return;
        }
        setIsConnected(true);
        setIsReconnecting(false);
        retryCountRef.current = 0;
        wsRef.current = ws;
      };

      ws.onmessage = handleMessage;

      ws.onclose = () => {
        if (!mountedRef.current) return;
        setIsConnected(false);
        wsRef.current = null;

        if (!intentionalCloseRef.current) {
          attemptReconnect();
        }
      };

      ws.onerror = () => {
        // onclose will fire after onerror, triggering reconnection logic
      };
    } catch {
      // Ticket request failed — trigger reconnect
      if (!mountedRef.current) return;
      setIsConnected(false);
      if (!intentionalCloseRef.current) {
        attemptReconnect();
      }
    }
  }, [requestTicket, wsBaseUrl, handleMessage, attemptReconnect]);

  /** Public connect: initiates the connection lifecycle */
  const connect = useCallback(() => {
    intentionalCloseRef.current = false;
    retryCountRef.current = 0;
    seqRef.current = 0;
    ackedSeqRef.current = 0;
    connectInternal();
  }, [connectInternal]);

  /** Public disconnect: closes the WebSocket intentionally */
  const disconnect = useCallback(() => {
    intentionalCloseRef.current = true;
    setIsReconnecting(false);

    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    setIsConnected(false);
  }, []);

  /** Send a message with auto-incremented sequence number */
  const sendMessage = useCallback(
    (action: ClientAction, payload?: Record<string, unknown>) => {
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        return;
      }

      seqRef.current += 1;
      const message: ClientMessage = {
        action,
        seq: seqRef.current,
        ...payload,
      } as ClientMessage;

      wsRef.current.send(JSON.stringify(message));
    },
    []
  );

  /** Cleanup on unmount */
  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      intentionalCloseRef.current = true;

      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }

      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, []);

  return {
    connect,
    disconnect,
    sendMessage,
    lastMessage,
    isConnected,
    isReconnecting,
  };
}
